import {
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Box
} from '@material-ui/core'
import { get, isEmpty } from 'lodash'
import React, { useEffect, useState } from 'react'

import {
  fetchCuratedDiscounts,
  fetchCanApplyCustomDiscount
} from 'api'

import * as Styles from './styles'

// Feature 4 — discount payload `type` field disambiguates the three shapes
// the backend accepts in apps/commons/discount/services/cart_discount.py:
//   - undefined / 'catalog' → legacy AppConfig dropdown selection
//   - 'custom_percentage'  → custom % entry, requires reason
//   - 'custom_value'       → custom ₹ entry, requires reason
const DISCOUNT_TYPE_CUSTOM_PERCENTAGE = 'custom_percentage'
const DISCOUNT_TYPE_CUSTOM_VALUE = 'custom_value'

const CUSTOM_PERCENTAGE_OPTION = {
  name: 'Custom',
  percentage: null,
  label: 'Enter a custom discount (%)',
  type: DISCOUNT_TYPE_CUSTOM_PERCENTAGE,
  // The backend cart_discount helper branches on `kind`. Tag it here so
  // the transformer doesn't have to derive it from `type`.
  kind: 'percentage'
}

const CUSTOM_VALUE_OPTION = {
  name: 'Custom',
  value: null,
  label: 'Enter a custom discount (₹)',
  type: DISCOUNT_TYPE_CUSTOM_VALUE,
  kind: 'value'
}

const isCustomDiscount = (discount) =>
  discount?.type === DISCOUNT_TYPE_CUSTOM_PERCENTAGE ||
  discount?.type === DISCOUNT_TYPE_CUSTOM_VALUE

const AdditionalDiscount = ({
  currentBatchInfo,
  onBatchInfoChange,
  batchDetails
}) => {
  // Feature 4 — authority lookup moved off a hardcoded phone-number list
  // (OVERRIDE_ADDITIONAL_DISCOUNT_NUMBERS) and onto the backend `me` endpoint
  // which reads AppConfig.DISCOUNT_AUTHORIZED_PHONE_NUMBERS. The cart
  // serializer re-checks on submission so this is purely a UI affordance.
  const [canGiveAdditionalDiscount, setCanGiveAdditionalDiscount] =
    useState(false)
  const [additionalDiscounts, setAdditionalDiscounts] = useState([])
  const [disabled, setDisabled] = useState(false)

  useEffect(() => {
    setDisabled(false)
  }, [])

  // Resolve the permission flag once on mount. Backend is the source of
  // truth; on network failure we default to false (fail-closed).
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const allowed = await fetchCanApplyCustomDiscount()
      if (!cancelled) setCanGiveAdditionalDiscount(allowed)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    ;(async () => {
      // Feature 4 — curated `Discount` rows from /commons/discounts/catalog/.
      // Replaces the dead AppConfig['PRESCHOOL_ADDITIONAL_DISCOUNTS'] feed.
      // Charmi #2/#10 (2026-05-26) — preschool batches carry batchDetails.type
      // 'school' or 'care' (the cart-item product type), but the curated
      // catalog rows are tagged program_type='preschool'. Pass 'preschool'
      // for both so Full Payment / Sibling rows actually surface; the cart
      // dropdown was returning [] for school batches and the user only saw
      // Custom %/₹ if whitelisted (and an empty "No additional discounts
      // available" placeholder otherwise).
      const rawType =
        batchDetails?.programType || batchDetails?.type || 'preschool'
      const programType =
        rawType === 'school' || rawType === 'care' ? 'preschool' : rawType
      const rows = await fetchCuratedDiscounts({ programType })

      // Sibling is auto-applied by the backend based on family enrolment
      // (it lives in the catalog so Finance/ops can edit it, but isn't a
      // user-selectable "additional" discount). Drop it from the dropdown.
      const selectable = rows.filter((row) => row.name !== 'Sibling')

      // Normalize to the shape `cart_discount.py::_is_catalog_payload`
      // recognises (`discount_uuid` is the key the backend matches on).
      // The other fields are kept for FE display only.
      const normalizedCatalog = selectable.map((row) => ({
        discount_uuid: row.uuid,
        label: row.label_name || row.name,
        name: row.name,
        kind: row.kind,
        percentage: row.percentage,
        value: row.value,
        type: row.type
      }))

      const discounts = [
        ...normalizedCatalog,
        ...(canGiveAdditionalDiscount
          ? [CUSTOM_PERCENTAGE_OPTION, CUSTOM_VALUE_OPTION]
          : [])
      ]
      setAdditionalDiscounts(discounts)

      if (
        discounts.length === 1 &&
        !isEmpty(currentBatchInfo?.paymentCycle?.value)
      ) {
        onBatchInfoChange(
          batchDetails?.uuid,
          'additionalDiscount',
          discounts[0]
        )
        setDisabled(true)
      } else {
        onBatchInfoChange(batchDetails?.uuid, 'additionalDiscount', null)
      }
    })()
  }, [
    batchDetails?.programType,
    batchDetails?.type,
    currentBatchInfo?.paymentCycle?.value,
    canGiveAdditionalDiscount
  ])

  // Custom-discount entry — write percentage or value plus the reason
  // back to the cart state. Reason is required at submit; we don't gate
  // typing on it but the backend will reject a payload with < 5 chars.
  const handleCustomFieldChange = (field, raw) => {
    const current = get(currentBatchInfo, 'additionalDiscount', null)
    if (!current || !isCustomDiscount(current)) return
    const next = { ...current }

    if (field === 'amount') {
      if (raw === '' || raw === '.') {
        next.percentage = null
        next.value = null
      } else {
        const parsed = parseFloat(raw)
        if (Number.isNaN(parsed)) return
        if (current.type === DISCOUNT_TYPE_CUSTOM_PERCENTAGE) {
          if (parsed < 0 || parsed > 100) return
          next.percentage = parsed
          next.value = null
        } else {
          if (parsed < 0) return
          next.value = parsed
          next.percentage = null
        }
      }
    } else if (field === 'reason') {
      next.reason = raw
    }

    onBatchInfoChange(batchDetails?.uuid, 'additionalDiscount', next)
  }

  const handleAdditionalDiscountChange = (val) => {
    let discounts = additionalDiscounts.filter((item) => item.label === val)
    onBatchInfoChange(batchDetails?.uuid, 'additionalDiscount', discounts[0])
  }

  const selected = get(currentBatchInfo, 'additionalDiscount', null)
  const showCustomInputs = isCustomDiscount(selected)
  const customAmount =
    selected?.type === DISCOUNT_TYPE_CUSTOM_VALUE
      ? selected?.value ?? ''
      : selected?.percentage ?? ''
  const customAmountStep =
    selected?.type === DISCOUNT_TYPE_CUSTOM_VALUE ? 1 : 0.01
  const customAmountMax =
    selected?.type === DISCOUNT_TYPE_CUSTOM_VALUE ? undefined : 100
  const customAmountLabel =
    selected?.type === DISCOUNT_TYPE_CUSTOM_VALUE
      ? 'Amount in ₹'
      : 'Percentage'

  return !isEmpty(get(currentBatchInfo, 'paymentCycle', '')) ? (
    <>
      <FormControl>
        <FormControlLabel
          value={get(currentBatchInfo, 'additionalDiscountOffered', false)}
          control={<Checkbox color="primary" />}
          label="Offer additional discount?"
          onChange={(e) => {
            onBatchInfoChange(
              batchDetails?.uuid,
              'additionalDiscountOffered',
              e.target.checked
            )

            if (!e.target.checked && canGiveAdditionalDiscount)
              onBatchInfoChange(batchDetails?.uuid, 'additionalDiscount', null)
          }}
        />
      </FormControl>
      {get(currentBatchInfo, 'additionalDiscountOffered', false) ? (
        <FormControl
          fullWidth
          variant="outlined"
          disabled={disabled}
          size="small"
        >
          <InputLabel id="additional-discount-label">Select a value</InputLabel>
          <Select
            labelId="additional-discount-label"
            value={selected?.label}
            onChange={(e) => handleAdditionalDiscountChange(e.target.value)}
            label="Select a value"
          >
            {additionalDiscounts.length === 0 ? (
              <MenuItem value="" disabled>
                No additional discounts available
              </MenuItem>
            ) : (
              additionalDiscounts.map((discount) => {
                const splits = discount.label?.split('_')
                return (
                  <MenuItem key={discount.label} value={discount.label}>
                    {splits?.map((part, index) => {
                      const isBold = index % 2 === 1
                      return isBold ? (
                        <Box fontFamily="AvenirDemi" display="inline">
                          {part}&nbsp;
                        </Box>
                      ) : (
                        <Box fontFamily="Avenir" display="inline">
                          {part}{' '}
                        </Box>
                      )
                    })}
                  </MenuItem>
                )
              })
            )}
          </Select>
        </FormControl>
      ) : null}
      {showCustomInputs ? (
        <>
          <FormControl
            fullWidth
            variant="outlined"
            disabled={disabled}
            size="small"
          >
            <TextField
              value={customAmount}
              onChange={(e) =>
                handleCustomFieldChange('amount', e.target.value)
              }
              placeholder={customAmountLabel}
              variant="standard"
              size="small"
              type="number"
              label={customAmountLabel}
              InputProps={{
                inputProps: {
                  min: 0,
                  max: customAmountMax,
                  step: customAmountStep
                }
              }}
              onKeyPress={(event) => {
                if (
                  event?.key === '-' ||
                  event?.key === '+' ||
                  event?.key === ','
                ) {
                  event.preventDefault()
                }
              }}
            />
          </FormControl>
          <FormControl fullWidth disabled={disabled} size="small">
            <TextField
              value={selected?.reason ?? ''}
              onChange={(e) =>
                handleCustomFieldChange('reason', e.target.value)
              }
              placeholder="Reason for custom discount (min 5 chars)"
              variant="standard"
              size="small"
              type="text"
              label="Reason (required)"
              required
              error={
                Boolean(selected?.reason) && selected.reason.trim().length < 5
              }
              helperText={
                Boolean(selected?.reason) && selected.reason.trim().length < 5
                  ? 'At least 5 characters required'
                  : ''
              }
            />
          </FormControl>
        </>
      ) : null}
    </>
  ) : null
}

export default AdditionalDiscount
